// Localization Service for SwipeSage
import { SUPPORTED_LANGUAGES, LanguageCode, DEFAULT_LANGUAGE } from '../constants/languages';
import { APP_STRINGS, formatString } from '../constants/strings';

// Translation interface
export interface Translation {
  [key: string]: string | Translation;
}

// Localization context interface
export interface LocalizationContext {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string, values?: Record<string, any>) => string;
  formatCurrency: (amount: number, currency?: string) => string;
  formatDate: (date: Date, format?: string) => string;
  formatNumber: (number: number, options?: Intl.NumberFormatOptions) => string;
  isRTL: boolean;
}

class LocalizationService {
  private currentLanguage: LanguageCode = DEFAULT_LANGUAGE;
  private translations: Map<LanguageCode, Translation> = new Map();
  private listeners: Set<(language: LanguageCode) => void> = new Set();

  constructor() {
    this.initializeTranslations();
    this.loadLanguageFromStorage();
  }

  // Initialize translations for all supported languages
  private initializeTranslations(): void {
    // For now, we'll use English as the base language
    // In a real implementation, you would load translations from files or API
    this.translations.set('en', this.flattenObject(APP_STRINGS));
    
    // Placeholder for other languages - in production, load from translation files
    const supportedLanguages = Object.keys(SUPPORTED_LANGUAGES) as LanguageCode[];
    supportedLanguages.forEach(lang => {
      if (lang !== 'en') {
        // For now, use English as fallback
        // In production, load actual translations
        this.translations.set(lang, this.flattenObject(APP_STRINGS));
      }
    });
  }

  // Flatten nested object for easier key access
  private flattenObject(obj: any, prefix = ''): Translation {
    const flattened: Translation = {};
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          Object.assign(flattened, this.flattenObject(obj[key], newKey));
        } else {
          flattened[newKey] = obj[key];
        }
      }
    }
    
    return flattened;
  }

  // Load language preference from localStorage
  private loadLanguageFromStorage(): void {
    try {
      const savedLanguage = localStorage.getItem('swipesage_language') as LanguageCode;
      if (savedLanguage && SUPPORTED_LANGUAGES[savedLanguage]) {
        this.currentLanguage = savedLanguage;
      }
    } catch (error) {
      console.warn('Failed to load language from storage:', error);
    }
  }

  // Save language preference to localStorage
  private saveLanguageToStorage(language: LanguageCode): void {
    try {
      localStorage.setItem('swipesage_language', language);
    } catch (error) {
      console.warn('Failed to save language to storage:', error);
    }
  }

  // Get current language
  getLanguage(): LanguageCode {
    return this.currentLanguage;
  }

  // Set language
  setLanguage(language: LanguageCode): void {
    if (!SUPPORTED_LANGUAGES[language]) {
      console.warn(`Unsupported language: ${language}`);
      return;
    }

    this.currentLanguage = language;
    this.saveLanguageToStorage(language);
    
    // Notify listeners
    this.listeners.forEach(listener => listener(language));
    
    // Update document direction for RTL languages
    this.updateDocumentDirection(language);
  }

  // Update document direction for RTL languages
  private updateDocumentDirection(language: LanguageCode): void {
    const languageConfig = SUPPORTED_LANGUAGES[language];
    if (languageConfig) {
      document.documentElement.dir = languageConfig.direction;
      document.documentElement.lang = language;
    }
  }

  // Translate a key
  t(key: string, values?: Record<string, any>): string {
    const translation = this.translations.get(this.currentLanguage);
    let text = translation?.[key] || this.translations.get('en')?.[key] || key;
    
    // Format string with values if provided
    if (values && typeof text === 'string') {
      text = formatString(text, values);
    }
    
    return text as string;
  }

  // Format currency
  formatCurrency(amount: number, currency = 'INR'): string {
    const languageConfig = SUPPORTED_LANGUAGES[this.currentLanguage];
    const locale = languageConfig?.code || 'en';
    
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
      }).format(amount);
    } catch (error) {
      // Fallback formatting
      return `${currency} ${amount.toLocaleString()}`;
    }
  }

  // Format date
  formatDate(date: Date, format = 'short'): string {
    const languageConfig = SUPPORTED_LANGUAGES[this.currentLanguage];
    const locale = languageConfig?.code || 'en';
    
    try {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: format === 'long' ? 'long' : 'short',
        day: 'numeric',
      };
      
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch (error) {
      // Fallback formatting
      return date.toLocaleDateString();
    }
  }

  // Format number
  formatNumber(number: number, options?: Intl.NumberFormatOptions): string {
    const languageConfig = SUPPORTED_LANGUAGES[this.currentLanguage];
    const locale = languageConfig?.code || 'en';
    
    try {
      return new Intl.NumberFormat(locale, options).format(number);
    } catch (error) {
      // Fallback formatting
      return number.toLocaleString();
    }
  }

  // Check if current language is RTL
  isRTL(): boolean {
    const languageConfig = SUPPORTED_LANGUAGES[this.currentLanguage];
    return (languageConfig?.direction as string) === 'rtl';
  }

  // Get supported languages
  getSupportedLanguages() {
    return SUPPORTED_LANGUAGES;
  }

  // Add language change listener
  addLanguageChangeListener(listener: (language: LanguageCode) => void): () => void {
    this.listeners.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  // Load translations from API or file
  async loadTranslations(language: LanguageCode): Promise<void> {
    try {
      // In production, you would fetch translations from an API or load from files
      // For now, we'll use a placeholder
      console.log(`Loading translations for ${language}...`);
      
      // Example API call:
      // const response = await fetch(`/api/translations/${language}`);
      // const translations = await response.json();
      // this.translations.set(language, this.flattenObject(translations));
      
    } catch (error) {
      console.error(`Failed to load translations for ${language}:`, error);
    }
  }

  // Get translation keys for a specific section
  getSectionKeys(section: string): string[] {
    const translation = this.translations.get(this.currentLanguage) || this.translations.get('en') || {};
    return Object.keys(translation).filter(key => key.startsWith(section));
  }

  // Check if a translation key exists
  hasKey(key: string): boolean {
    const translation = this.translations.get(this.currentLanguage) || this.translations.get('en') || {};
    return key in translation;
  }

  // Get all available keys
  getAllKeys(): string[] {
    const translation = this.translations.get(this.currentLanguage) || this.translations.get('en') || {};
    return Object.keys(translation);
  }

  // Export translations for a language (useful for translation management)
  exportTranslations(language: LanguageCode): Translation {
    return this.translations.get(language) || {};
  }

  // Import translations for a language
  importTranslations(language: LanguageCode, translations: Translation): void {
    this.translations.set(language, translations);
  }
}

// Create singleton instance
export const localizationService = new LocalizationService();

// React hook for localization (for React components)
export function useLocalization(): LocalizationContext {
  // This would be implemented in a React context
  // For now, return the service methods
  return {
    language: localizationService.getLanguage(),
    setLanguage: (language: LanguageCode) => localizationService.setLanguage(language),
    t: (key: string, values?: Record<string, any>) => localizationService.t(key, values),
    formatCurrency: (amount: number, currency?: string) => localizationService.formatCurrency(amount, currency),
    formatDate: (date: Date, format?: string) => localizationService.formatDate(date, format),
    formatNumber: (number: number, options?: Intl.NumberFormatOptions) => localizationService.formatNumber(number, options),
    isRTL: localizationService.isRTL(),
  };
}

// Utility functions for non-React usage
export const t = (key: string, values?: Record<string, any>) => localizationService.t(key, values);
export const formatCurrency = (amount: number, currency?: string) => localizationService.formatCurrency(amount, currency);
export const formatDate = (date: Date, format?: string) => localizationService.formatDate(date, format);
export const formatNumber = (number: number, options?: Intl.NumberFormatOptions) => localizationService.formatNumber(number, options);

export default localizationService; 